'use client';

import { useEffect, useState } from 'react';

/**
 * Polling interval for the countdown — 60 seconds. Balances responsiveness
 * ("STARTS IN 2H" tick) against render churn on the event detail page.
 */
const POLL_INTERVAL_MS = 60_000;

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Compute the countdown label for a given event start time.
 *
 * Returns:
 * - `'STARTS IN Xh'` when the event starts within the next 24 hours (and has
 *   not started yet).
 * - `'TONIGHT'` when the event is in the future and falls on the same
 *   calendar day as `now`.
 * - `'TOMORROW'` when the event is on the next calendar day.
 * - `null` for any other case (past event, invalid date, null input, or more
 *   than ~48 hours out).
 *
 * Calendar-day comparison uses local time — acceptable for a discovery UI
 * where "tonight" / "tomorrow" tracks the viewer's wall clock. Callers that
 * need strict venue-local framing should format their own copy.
 */
export function computeStartsIn(
  eventDate: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!eventDate) return null;

  const start = new Date(eventDate);
  const startMs = start.getTime();
  if (Number.isNaN(startMs)) return null;

  const nowMs = now.getTime();
  const delta = startMs - nowMs;

  // Past (or exactly now) — hide the pill.
  if (delta <= 0) return null;

  // < 24h out → STARTS IN Xh. Clamp to 1–23 so we never surface "24H" (a
  // near-24h delta would round up to 24 via Math.ceil, which reads wrong next
  // to the TOMORROW branch).
  if (delta < MS_PER_DAY) {
    const hours = Math.min(23, Math.max(1, Math.ceil(delta / MS_PER_HOUR)));
    return `STARTS IN ${hours}H`;
  }

  // Same calendar day as now — unusual past the < 24h check, but covers the
  // edge where delta is ≥ 24h but still today (e.g. 23h from early-morning
  // "now" to late-evening start of the same day is already handled above;
  // this branch catches timezone drift).
  if (isSameLocalDay(start, now)) {
    return 'TONIGHT';
  }

  // Next calendar day → TOMORROW.
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (isSameLocalDay(start, tomorrow)) {
    return 'TOMORROW';
  }

  return null;
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Live countdown hook — returns `computeStartsIn(eventDate)` and re-computes
 * every {@link POLL_INTERVAL_MS} until unmount. Returns `null` for past
 * events or when no date is provided, so consumers can hide the UI cleanly.
 */
export function useStartsIn(eventDate: string | null | undefined): string | null {
  const [label, setLabel] = useState<string | null>(() => computeStartsIn(eventDate));

  useEffect(() => {
    setLabel(computeStartsIn(eventDate));

    if (!eventDate) return;

    const intervalId = setInterval(() => {
      setLabel(computeStartsIn(eventDate));
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [eventDate]);

  return label;
}
